import React, { useState, useEffect, useMemo } from "react";
import Swal from 'sweetalert2';
import { useForm } from "react-hook-form";
import { useAlerts } from '../../../../hooks/useAlerts';
import { sqlDateFormat } from '../../../../utils/Common';
import NotifyC from '../../../../components/Notifications';
import Spinner from "../../../../components/Spinner";
import { BsSignal, BsCalendar2Check } from "react-icons/bs";
import { TbZoomReset } from "react-icons/tb";
import { ImCheckboxUnchecked, ImCheckboxChecked } from "react-icons/im";
import { RiRefreshLine } from 'react-icons/ri';
import DataTable from "../../../../components/ui/DataTable";
import SelectSearch from "../../../../components/ui/SelectSearch";
import * as IoIcons from "react-icons/io";

const SearchAlerts = () => {
    const { getAllAlerts, readAlerts } = useAlerts();
  
    const [messages, setMessages] = useState([]);
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [loadingRow, setLoadingRow] = useState(null); 
    const [reload, setReload] = useState(false); 

    const handleGetMessageUser = async () => {
        const abortController = new AbortController();
        try {
            const response = await getAllAlerts(abortController.signal);
            const dataOrder = response.sort((a, b) => new Date(b?.FechaRegistro) - new Date(a?.FechaRegistro));
            setMessages(dataOrder);
            setFilteredMessages(dataOrder);
        } catch (error) {
            setMessages([]);
            setFilteredMessages([]);
            console.error('Error fetching messages:', error);
        }
    };

    const handleReload = async () => {
        setReload(true); 
        await handleGetMessageUser();
        NotifyC.success('Alertas actualizadas.', { duration: 3000, position: 'top-right' });
        setReload(false);
    };

    const formatStatusAlert = (status) => {
        switch (status) {
            case 1:
                return "No leído";
            case 2:
                return "Leído";
            default:
                return "Desconocido";
        }
    };

    const formatTypeAlert = (type) => {
        switch (type) {
            case 1:
                return "SALDO DIFERENTE EN CIERRE CAJA";
            case 2:
                return "SALDO DIFERENTE EN APERTURA CAJA";
            default:
                return "Desconocido";
        }
    };

    const handleReadAlert = async (id) => {
        const result = await Swal.fire({
            title: 'Confirmar lectura de alerta',
            text: '¿Estás seguro de que deseas confirmar la lectura de esta alerta?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: "white",
            cancelButtonColor: "black",
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            customClass: {
                confirmButton: 'custom-confirm-button',
                cancelButton: 'custom-cancel-button',
            },
        });

        if (!result.isConfirmed) return;

        setLoadingRow(id); 

        const dataToSend = { id, type: 2 };
        const abortController = new AbortController();
        const response = await readAlerts(abortController.signal, dataToSend);
        
        if (response.code === 200) {
            handleGetMessageUser();
            NotifyC.success("Confirmación de lectura de alerta", { duration: 6000 });
            setLoadingRow(null); 
        } else {
            if (response.code === 404) {
                switch (response.message) {
                    case "ALERT_ALREADY_CHECKED":
                        Swal.fire({
                            title: '¡Error!',
                            text: 'Esta alerta ya ha sido confirmada como leída',
                            icon: 'error',
                            confirmButtonColor: "white",
                            customClass: { confirmButton: 'custom-confirm-button' },
                        });
                        break;
                    case "ERROR_READ_ALERTS":
                        Swal.fire({
                            title: '¡Error!',
                            text: 'No se pudo confirmar la lectura de la alerta',
                            icon: 'error',
                            confirmButtonColor: "white",
                            customClass: { confirmButton: 'custom-confirm-button' },
                        });
                }
                setLoadingRow(null); 
            }
        }

        
    };

    const handleStatusChange = (e) => {
        const status = e.target.value;
        setSelectedStatus(status);
        filterMessages(status, selectedDate);
    };

    const handleDateChange = (e) => {
        const date = e.target.value;
        setSelectedDate(date);
        filterMessages(selectedStatus, date);
    };

    const filterMessages = (status, date) => {
        let filtered = messages;

        if (status) {
            filtered = filtered.filter(message => message.IdEstatus === parseInt(status));
        }

        if (date) {
            filtered = filtered.filter(message => new Date(message.FechaRegistro).toISOString().split('T')[0] === date);
        }

        setFilteredMessages(filtered);
    };

    const handleResetFilters = () => {
        setSelectedStatus("");
        setSelectedDate("");
        setFilteredMessages(messages);
    };

    const columns = useMemo(
        () => [
        { Header: "Nro. Alerta", accessor: "IdAlertas", exportable: true },
        { Header: "Mensaje de alerta", accessor: "Descripcion", exportable: true },
        { Header: "Tipo de alerta", accessor: "IdTipoAlerta", Cell: ({ value }) => formatTypeAlert(value), exportable: true },
        { Header: "Estado", accessor: "IdEstatus", Cell: ({ value }) => formatStatusAlert(value), exportable: true },
        { Header: "Fecha", accessor: "FechaRegistro", Cell: ({ value }) => sqlDateFormat(value, false, true), exportable: true },
        { Header: "Leida por", accessor: "ClienteLectura", Cell: ({ value }) => value ? value : "Sin leer", exportable: true },
        {
            Header: "Marcar como leída",
            accessor: "IdClienteLectura",
            Cell: ({ row }) => (
                <button
                    className="btn btn-blue"
                    onClick={() => handleButtonClick(row.original)}
                    disabled={row.original.IdEstatus !== 1  || loadingRow === row.original.IdAlertas}
                >
                    {loadingRow === row.original.IdAlertas ? (
                        <Spinner type={1} size={20} color="white" />
                    ) : row.original.IdEstatus === 1 ? (
                        <ImCheckboxUnchecked size={20} />
                    ) : row.original.ClienteLectura ? (
                        <ImCheckboxChecked size={20} />
                    ) : "Leído"
                    }
                </button>
            ),
            exportable: false,
        }
    ], [loadingRow]);

    const exportableColumns = columns.filter(column => column.exportable);

    const handleButtonClick = (row) => {
        handleReadAlert(row.IdAlertas);
    };

    const LoadingSpinner = () => (
        <div
            style={{
                position: "absolute",
                top: 0,
                padding: 20,
                right: 0,
                left: 0,
                backgroundColor: "rgb(0 0 0 / 81%)",
                height: "100vh",
                zIndex: 1000,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "black",
            }}
            className="text-left"
        >
            <Spinner type={1} size={40} color="white" />
        </div>
    );

    useEffect(() => {
        handleGetMessageUser();
    }, []);

    return (
        <>
            <div className="dashboardCard justify-left" style={{ color: "black" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div className="d-flex align-items-center" style={{ gap: 15 }}>
                        <IoIcons.IoMdNotificationsOutline size={25} />
                        <h5 className="mb-0">Alertas</h5>
                    </div>
                    <RiRefreshLine 
                        size={24} 
                        style={{ cursor: "pointer", animation: reload ? "spin 2s linear infinite" : "none" }} 
                        onClick={handleReload} 
                    />
                </div>
            </div>
            {messages.length > 0 ? (
                <>
                    <div className="dashboardCard p-3" style={{ color: "black" }}>
                        <div className="d-flex justify-content-between flex-wrap mb-2">
                            <p className="mb-0">Filtros de búsqueda</p>
                        </div>
                        <div className="row g-2">
                            <div className="col-md-6">
                                <div className="input-group w-100">
                                    <div className="input-group-prepend">
                                        <span className="input-group-text text-bold" style={{
                                            backgroundColor: "#FBD825",
                                            color: "black",
                                            borderRadius: "6px 0 0 6px",
                                        }}>
                                            <BsSignal className="mr-2" size={18} />Estado
                                        </span>
                                    </div>
                                        <select
                                        id="statusFilter"
                                        className="form-control"
                                        value={selectedStatus}
                                        onChange={handleStatusChange}
                                    >
                                        <option value="">Todos</option>
                                        <option value="1">No leído</option>
                                        <option value="2">Leído</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="input-group w-100">
                                    <div className="input-group-prepend">
                                        <span className="input-group-text text-bold" style={{
                                            backgroundColor: "#FBD825",
                                            color: "black",
                                            borderRadius: "6px 0 0 6px",
                                        }}>
                                            <BsCalendar2Check className="mr-2" size={18} />Fecha
                                        </span>
                                    </div>
                                    <input
                                        type="date"
                                        id="dateFilter"
                                        className="form-control"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                    />
                                </div>
                            </div>
                        </div>
                        {(selectedStatus || selectedDate) && (
                            <div className="d-flex justify-content-center mt-3" style={{gap:"12px"}}>
                                <button type="reset" className="btn btn-secondary" onClick={handleResetFilters}>
                                    <TbZoomReset size={20}/> Reiniciar
                                </button>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <LoadingSpinner />
            )}
            {messages.length > 0 && (
                <div className="dashboardCard mt-3">
                    <DataTable
                        columns={columns}
                        data={filteredMessages}
                        tableTitle="Resultados de Alertas"
                        filename="alertas-remesas-cotel"
                        exportExcel
                        exportPdf
                        exportableColumns={exportableColumns}
                        nameReport='REPORTE ALERTAS'
                        col={1.5}
                        row={0}
                    />
                </div>
            )}
        </>
    );
};

export default SearchAlerts;